<template>
  <section class="calculator-module">
    <div class="module-header">
      <h2>{{ t('aiAdvisor.title') }}</h2>
      <span class="toggle-icon">▼</span>
    </div>
    <div class="module-content">
      <div class="ai-intro">
        <h3>{{ t('aiAdvisor.subtitle') }}</h3>
        <p>{{ t('aiAdvisor.description') }}</p>
      </div>
      
      <!-- 导入最近数据按钮 -->
      <div v-if="showImportButton" class="import-section">
        <button
          type="button"
          class="import-btn"
          @click="openImportDialog"
        >
          {{ t('aiAdvisor.buttons.importRecentData') }}
        </button>
      </div>

      <form @submit.prevent="submitForm" class="ai-form">
        <div class="form-section">
          <h4>{{ t('aiAdvisor.sections.basicInfo') }}</h4>
          <div class="form-group">
            <label for="volume">{{ t('aiAdvisor.labels.volume') }}</label>
            <input
              type="number"
              id="volume"
              v-model="formData.volume"
              class="form-control"
              :placeholder="t('aiAdvisor.placeholders.volume').value"
              step="0.1"
              min="1"
            >
          </div>
          <div class="form-group">
            <label for="tank_type">{{ t('aiAdvisor.labels.tankType') }}</label>
            <select
              id="tank_type"
              v-model="formData.tank_type"
              class="select-control"
            >
              <option value="">{{ t('aiAdvisor.placeholders.tankTypeSelect') }}</option>
              <option value="ONLY SPS">{{ t('aiAdvisor.tankTypeOptions.fullSPS') }}</option>
              <option value="ONLY LPS">{{ t('aiAdvisor.tankTypeOptions.fullLPS') }}</option>
              <option value="SPS/LPS MIX">{{ t('aiAdvisor.tankTypeOptions.mixedSPSLPS') }}</option>
              <option value="NPS">{{ t('aiAdvisor.tankTypeOptions.nps') }}</option>
              <option value="FOT">{{ t('aiAdvisor.tankTypeOptions.fot') }}</option>
            </select>
          </div>
        </div>
        
        <div class="form-section">
          <h4>{{ t('aiAdvisor.sections.waterParams') }}</h4>
          <div class="form-row">
            <div class="form-group">
              <label for="alk">{{ t('aiAdvisor.labels.alk') }}</label>
              <input
                type="number"
                id="alk"
                v-model="formData.alk"
                class="form-control"
                step="0.1"
                :placeholder="t('aiAdvisor.placeholders.alk').value"
                min="0"
                max="20"
              >
            </div>
            <div class="form-group">
              <label for="ca">{{ t('aiAdvisor.labels.ca') }}</label>
              <input
                type="number"
                id="ca"
                v-model="formData.ca"
                class="form-control"
                :placeholder="t('aiAdvisor.placeholders.ca').value"
                min="0"
                max="1000"
              >
            </div>
            <div class="form-group">
              <label for="mg">{{ t('aiAdvisor.labels.mg') }}</label>
              <input
                type="number"
                id="mg"
                v-model="formData.mg"
                class="form-control"
                :placeholder="t('aiAdvisor.placeholders.mg').value"
                min="0"
                max="2000"
              >
            </div>
          </div>
          <div class="form-row">
            <div class="form-group">
              <label for="no3">{{ t('aiAdvisor.labels.no3') }}</label>
              <input
                type="number"
                id="no3"
                v-model="formData.no3"
                class="form-control"
                step="0.1"
                :placeholder="t('aiAdvisor.placeholders.no3').value"
                min="0"
                max="100"
              >
            </div>
            <div class="form-group">
              <label for="po4">{{ t('aiAdvisor.labels.po4') }}</label>
              <input
                type="number"
                id="po4"
                v-model="formData.po4"
                class="form-control"
                step="0.001"
                :placeholder="t('aiAdvisor.placeholders.po4').value"
                min="0"
                max="1"
              >
            </div>
            <div class="form-group">
              <label for="ph">{{ t('aiAdvisor.labels.ph') }}</label>
              <input
                type="number"
                id="ph"
                v-model="formData.ph"
                class="form-control"
                step="0.1"
                :placeholder="t('aiAdvisor.placeholders.ph').value"
                min="7"
                max="9"
              >
            </div>
          </div>
          <div class="form-group">
            <label for="ai-salinity">{{ t('aiAdvisor.labels.salinity') }}</label>
            <input
              type="number"
              id="ai-salinity"
              v-model="formData.salinity"
              class="form-control"
              step="0.001"
              :placeholder="t('aiAdvisor.placeholders.salinity').value"
              min="1.015"
              max="1.040"
            >
          </div>
        </div>
        
        <div class="form-section">
          <h4>{{ t('aiAdvisor.sections.environment') }}</h4>
          <div class="form-group">
            <label for="lighting">{{ t('aiAdvisor.labels.lighting') }}</label>
            <input
              type="text"
              id="lighting"
              v-model="formData.lighting"
              class="form-control"
              :placeholder="t('aiAdvisor.placeholders.lighting').value"
            >
          </div>
          <div class="form-group">
            <label for="flow">{{ t('aiAdvisor.labels.flow') }}</label>
            <input
              type="text"
              id="flow"
              v-model="formData.flow"
              class="form-control"
              :placeholder="t('aiAdvisor.placeholders.flow').value"
            >
          </div>
          <div class="form-group">
            <label for="water_change">{{ t('aiAdvisor.labels.waterChange') }}</label>
            <input
              type="text"
              id="water_change"
              v-model="formData.water_change"
              class="form-control"
              :placeholder="t('aiAdvisor.placeholders.waterChange').value"
            >
          </div>
          <div class="form-group">
            <label for="dosing">{{ t('aiAdvisor.labels.dosing') }}</label>
            <input
              type="text"
              id="dosing"
              v-model="formData.dosing"
              class="form-control"
              :placeholder="t('aiAdvisor.placeholders.dosing').value"
            >
          </div>
          <div class="form-group">
            <label for="status">{{ t('aiAdvisor.labels.coralStatus') }}</label>
            <textarea
              id="status"
              v-model="formData.status"
              class="form-control"
              rows="3"
              :placeholder="t('aiAdvisor.placeholders.coralStatus').value"
            ></textarea>
          </div>
          <div class="form-group">
            <label for="other">{{ t('aiAdvisor.labels.problem') }}</label>
            <textarea
              id="other"
              v-model="formData.other"
              class="form-control"
              rows="2"
              :placeholder="t('aiAdvisor.placeholders.problem').value"
            ></textarea>
          </div>
        </div>
        
        <!-- 提问计数器 -->
        <div v-if="questionCounter" class="question-counter">
          {{ t('aiAdvisor.counter', { remaining: remainingQuestions, max: maxQuestions }) }}
        </div>
        
        <button
          type="submit"
          class="submit-btn"
          :disabled="!canSubmit || isLoading"
        >
          <span v-if="isLoading" class="loading"></span>
          {{ isLoading ? t('aiAdvisor.buttons.analyzing') : t('aiAdvisor.buttons.getAnalysis') }}
        </button>
      </form>

      <!-- 结果容器 -->
      <div id="results-container" class="results-container">
        <div class="empty-state">
          <div class="empty-icon">📊</div>
          <h4>{{ t('aiAdvisor.emptyState.title') }}</h4>
          <p>{{ t('aiAdvisor.emptyState.description') }}</p>
        </div>
      </div>

      <!-- 导入数据对话框 -->
      <div v-if="showImportDialog" class="import-dialog-overlay">
        <div class="import-dialog">
          <div class="dialog-header">
            <h4>{{ t('aiAdvisor.importDialog.title') }}</h4>
            <button class="close-btn" @click="showImportDialog = false">×</button>
          </div>
          <div class="dialog-content">
            <p>{{ t('aiAdvisor.importDialog.message') }}</p>
            <div class="data-preview">
              <h5>{{ t('aiAdvisor.importDialog.previewTitle') }}</h5>
              <div v-if="importDataPreview.hasTankLogData" class="data-item">
                <span class="data-label">{{ t('aiAdvisor.importDialog.waterParamsLabel') }}</span>
                <span class="data-value">{{ t('aiAdvisor.importDialog.waterParamsValue') }}</span>
              </div>
              <div v-if="importDataPreview.hasPARData" class="data-item">
                <span class="data-label">{{ t('aiAdvisor.importDialog.lightingDataLabel') }}</span>
                <span class="data-value">{{ t('aiAdvisor.importDialog.lightingDataValue') }}</span>
              </div>
              <div v-if="!importDataPreview.hasTankLogData && !importDataPreview.hasPARData" class="no-data">
                {{ t('aiAdvisor.importDialog.noData') }}
              </div>
            </div>
          </div>
          <div class="dialog-actions">
            <button class="cancel-btn" @click="showImportDialog = false">{{ t('aiAdvisor.importDialog.buttons.cancel') }}</button>
            <button class="confirm-btn" @click="importRecentData" :disabled="!importDataPreview.hasTankLogData && !importDataPreview.hasPARData">
              {{ t('aiAdvisor.importDialog.buttons.confirm') }}
            </button>
          </div>
        </div>
      </div>

      <!-- 成功消息模态框 -->
      <div v-if="showSuccessModal" class="success-modal-overlay">
        <div class="success-modal">
          <div class="success-icon">✅</div>
          <div class="success-content">
            <h4>{{ t('aiAdvisor.successModal.title') }}</h4>
            <p>{{ successModalMessage }}</p>
          </div>
          <button class="success-close-btn" @click="showSuccessModal = false">
            {{ t('aiAdvisor.successModal.ok') }}
          </button>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ref, computed, reactive, onMounted } from 'vue'
import { indexedDBService, initIndexedDB, type TankRecord } from '../utils/indexedDB'
import { useI18n } from '../composables/useI18n'

const { t, language } = useI18n()

// 表单数据
const formData = reactive({
  volume: '',
  tank_type: '',
  alk: '',
  ca: '',
  mg: '',
  no3: '',
  po4: '',
  ph: '',
  salinity: '',
  lighting: '',
  flow: '',
  water_change: '',
  dosing: '',
  status: '',
  other: ''
})

// 导入功能状态
const showImportDialog = ref(false)
const showImportButton = ref(false)
const showSuccessModal = ref(false)
const successModalMessage = ref('')
const importDataPreview = ref({
  hasTankLogData: false,
  hasPARData: false
})

// 状态
const isLoading = ref(false)
const maxQuestions = 10
const questionCounter = ref(0)

// 计算属性
const hasRelevantData = computed(() => {
  return formData.alk || formData.ca || formData.no3 || formData.po4 || 
         formData.mg || formData.salinity || formData.tank_type
})

const canSubmit = computed(() => {
  return hasRelevantData.value && !isLoading.value && remainingQuestions.value > 0
})

const remainingQuestions = computed(() => {
  return Math.max(0, maxQuestions - questionCounter.value)
})

// 初始化提问计数器
const initializeQuestionCounter = () => {
  const today = new Date().toDateString()
  const storedData = localStorage.getItem('aiQuestionData')
  
  if (!storedData) {
    // 第一次使用，初始化数据
    const initialData = {
      date: today,
      count: 0
    }
    localStorage.setItem('aiQuestionData', JSON.stringify(initialData))
  } else {
    // 检查是否是同一天
    const data = JSON.parse(storedData)
    if (data.date !== today) {
      // 新的一天，重置计数器
      const newData = {
        date: today,
        count: 0
      }
      localStorage.setItem('aiQuestionData', JSON.stringify(newData))
    }
  }
}

// 获取当前提问次数
const getQuestionCount = (): number => {
  const storedData = localStorage.getItem('aiQuestionData')
  if (!storedData) return 0
  
  const data = JSON.parse(storedData)
  const today = new Date().toDateString()
  
  // 如果是新的一天，返回0
  if (data.date !== today) {
    return 0
  }
  
  return data.count || 0
}

// 增加提问次数
const incrementQuestionCount = () => {
  const today = new Date().toDateString()
  const storedData = localStorage.getItem('aiQuestionData')
  
  let data
  if (storedData) {
    data = JSON.parse(storedData)
    // 如果是新的一天，重置计数器
    if (data.date !== today) {
      data = { date: today, count: 0 }
    }
  } else {
    data = { date: today, count: 0 }
  }
  
  data.count += 1
  localStorage.setItem('aiQuestionData', JSON.stringify(data))
  questionCounter.value = data.count
}

// 检查是否超过提问限制
const hasExceededLimit = computed(() => {
  return questionCounter.value >= maxQuestions
})

// 显示限制提示信息
const displayLimitMessage = () => {
  const resultsContainer = document.getElementById('results-container')
  if (!resultsContainer) return

  // 移除空状态
  const emptyState = resultsContainer.querySelector('.empty-state')
  if (emptyState) {
    emptyState.remove()
  }

  resultsContainer.innerHTML = `
    <div class="limit-message">
      <h4>${t('aiAdvisor.errors.limitTitle').value}</h4>
      <p>${t('aiAdvisor.errors.limitUsed', { max: maxQuestions }).value}</p>
      <p>${t('aiAdvisor.errors.limitLimit', { max: maxQuestions }).value}</p>
      <p class="limit-note">${t('aiAdvisor.errors.limitNote').value}</p>
    </div>
  `
}

// 显示结果
const displayResult = (review: string) => {
  const resultsContainer = document.getElementById('results-container')
  if (!resultsContainer) return

  // 格式化AI返回的文本
  const formattedReview = formatReviewText(review)
  
  // 移除空状态
  const emptyState = resultsContainer.querySelector('.empty-state')
  if (emptyState) {
    emptyState.remove()
  }

  resultsContainer.innerHTML = `
    <div class="ai-review">
      <h4>${t('aiAdvisor.errors.reportTitle').value}</h4>
      <div class="review-content">${formattedReview}</div>
    </div>
  `
}

// 格式化AI返回的文本
const formatReviewText = (text: string): string => {
  // 将换行符转换为HTML段落
  return text
    .split('\n')
    .filter(line => line.trim())
    .map(line => {
      // 处理标题行
      if (line.includes('【') || line.includes('】') || line.includes('整体评估') || line.includes('关键亮点') || line.includes('风险诊断') || line.includes('具体建议')) {
        return `<h5>${line}</h5>`
      }
      // 处理普通段落
      return `<p>${line}</p>`
    })
    .join('')
}

// 显示错误
const displayError = (message: string) => {
  const resultsContainer = document.getElementById('results-container')
  if (!resultsContainer) return

  // 移除空状态
  const emptyState = resultsContainer.querySelector('.empty-state')
  if (emptyState) {
    emptyState.remove()
  }

  resultsContainer.innerHTML = `
    <div class="error-message">
      <p>${message}</p>
    </div>
  `
}

// 显示加载状态
const showLoading = () => {
  const resultsContainer = document.getElementById('results-container')
  if (!resultsContainer) return

  // 移除空状态
  const emptyState = resultsContainer.querySelector('.empty-state')
  if (emptyState) {
    emptyState.remove()
  }

  resultsContainer.innerHTML = `
    <div class="loading-spinner">
      <div class="spinner"></div>
      <p>${t('aiAdvisor.errors.loadingMessage').value}</p>
    </div>
  `
}

// 获取AI点评
const getReview = async (params: any) => {
  if (isLoading.value) return
  
  // 检查提问次数限制
  if (hasExceededLimit.value) {
    displayLimitMessage()
    return
  }
  
  isLoading.value = true
  showLoading()

  try {
    // 创建超时控制器，设置30秒超时
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 30000)

    const response = await fetch('/.netlify/functions/reef-review', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params),
      signal: controller.signal
    })

    clearTimeout(timeoutId)

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const data = await response.json()
    
    if (data.review) {
      // 成功获取回答后增加计数
      incrementQuestionCount()
      displayResult(data.review)
    } else {
      displayError(data.error || t('aiAdvisor.errors.aiError').value)
    }
  } catch (error: any) {
    console.error('AI顾问请求失败:', error)
    if (error.name === 'AbortError') {
      displayError(t('aiAdvisor.errors.timeout').value)
    } else if (error.name === 'TypeError' && error.message.includes('fetch')) {
      displayError(t('aiAdvisor.errors.network').value)
    } else {
      displayError(t('aiAdvisor.errors.serviceUnavailable').value)
    }
    // 注意：错误情况下不增加计数
  } finally {
    isLoading.value = false
  }
}

// 提交表单
const submitForm = () => {
  // 收集表单数据
  const params: any = {}
  Object.keys(formData).forEach(key => {
    const value = (formData as any)[key]
    if (value !== null && value !== undefined && value !== '') {
      // 如果是字符串，去除首尾空格；如果是数字，直接使用
      params[key] = typeof value === 'string' ? value.trim() : value
    }
  })

  // 添加当前语言
  if (language.value) {
    params.language = language.value
  }

  // 验证至少有一个水质相关字段
  const hasRelevantData =
    params.alk || params.ca || params.no3 || params.po4 ||
    params.mg || params.salinity || params.tank_type

  if (!hasRelevantData) {
    displayError(t('aiAdvisor.errors.noRelevantData').value)
    return
  }

  getReview(params)
}

// 获取最近一次缸体日志数据
const getLatestTankLogData = async () => {
  try {
    // 确保数据库已初始化
    await initIndexedDB()
    
    const records = await indexedDBService.getAllRecords()
    const waterRecords = records.filter((record: TankRecord) => record.type === 'water')
    
    if (waterRecords.length === 0) return null
    
    // 按时间戳排序，获取最新的记录
    const latestRecord = waterRecords.sort((a, b) => b.timestamp - a.timestamp)[0]
    
    // 加载照片数据
    if (latestRecord.type === 'water') {
      const photos = await indexedDBService.getPhotosByRecordId(latestRecord.id)
      latestRecord.data.photos = photos
    }
    
    return latestRecord.data
  } catch (error) {
    console.error('获取缸体日志数据失败:', error)
    return null
  }
}

// 获取最近一次PAR分析数据
const getLatestPARData = () => {
  try {
    const stored = localStorage.getItem('parAnalysisData')
    if (!stored) return null
    
    const parData = JSON.parse(stored)
    return parData
  } catch (error) {
    console.error('获取PAR数据失败:', error)
    return null
  }
}

// 导入数据到表单
const importRecentData = async () => {
  try {
    const tankLogData = await getLatestTankLogData()
    const parData = getLatestPARData()
    
    if (!tankLogData && !parData) {
      showSuccessModalMessage(t('aiAdvisor.successMessages.noDataFound').value)
      return
    }
    
    // 导入缸体日志数据
    if (tankLogData) {
      // 映射水质参数
      if (tankLogData.alkalinity) formData.alk = tankLogData.alkalinity.toString()
      if (tankLogData.calcium) formData.ca = tankLogData.calcium.toString()
      if (tankLogData.magnesium) formData.mg = tankLogData.magnesium.toString()
      if (tankLogData.nitrate) formData.no3 = tankLogData.nitrate.toString()
      if (tankLogData.phosphate) formData.po4 = tankLogData.phosphate.toString()
      if (tankLogData.ph) formData.ph = tankLogData.ph.toString()
      if (tankLogData.salinity) {
        // 现在缸体日志直接存储比重，直接使用
        formData.salinity = tankLogData.salinity.toString()
      }
    }
    
    // 导入PAR数据（如果有）
    if (parData) {
      // 导入灯具型号和PAR值范围到光照系统字段
      let lightingInfo = []
      
      // 添加灯具型号
      if (parData.lightType === 'led' && parData.ledBrand && parData.ledBrand !== 'custom') {
        const ledBrands: Record<string, string> = {
          aipai_a8_pro: '艾湃 A8 Pro',
          aipai_a7_3rd: '艾湃 A7 三代',
          kessil_a360x: 'Kessil A360X',
          ai_prime_hd: 'AI Prime HD',
          radion_xr15: 'EcoTech Radion XR15',
          radion_xr30: 'EcoTech Radion XR30'
        }
        const brandName = ledBrands[parData.ledBrand] || parData.ledBrand
        lightingInfo.push(brandName)
      } else if (parData.lightType) {
        const lightTypeNames: Record<string, string> = {
          led: 'LED',
          t5: 'T5荧光灯',
          metal_halide: '金卤灯'
        }
        const lightTypeName = lightTypeNames[parData.lightType] || parData.lightType
        lightingInfo.push(lightTypeName)
      }
      
      // 添加灯高和PAR值范围（优先显示PAR值范围，而不是实际测量值）
      if (parData.lightHeight) {
        // 基于灯具类型和功率计算PAR值范围
        let minPar = 0
        let maxPar = 0
        
        if (parData.lightType === 'led' && parData.ledBrand && parData.ledBrand !== 'custom') {
          // 使用品牌LED的实测数据
          const ledBrandsData: Record<string, { basePar: number, parAt30cm: number, parAt50cm: number }> = {
            aipai_a8_pro: { basePar: 532, parAt30cm: 369, parAt50cm: 240 },
            aipai_a7_3rd: { basePar: 265, parAt30cm: 120, parAt50cm: 65 },
            kessil_a360x: { basePar: 280, parAt30cm: 150, parAt50cm: 80 },
            ai_prime_hd: { basePar: 180, parAt30cm: 90, parAt50cm: 45 },
            radion_xr15: { basePar: 300, parAt30cm: 160, parAt50cm: 85 },
            radion_xr30: { basePar: 450, parAt30cm: 250, parAt50cm: 140 }
          }
          
          const brandData = ledBrandsData[parData.ledBrand]
          if (brandData) {
            // 根据灯高和测量深度估算PAR值范围
            const baseHeight = 15 // 基准高度
            const heightFactor = Math.pow(baseHeight / Math.max(parData.lightHeight, 5), 2)
            const powerFactor = Math.pow((parData.lightPowerPercent || 100) / 100, 0.8)
            
            // 计算中心点PAR值
            const centerPar = Math.round(brandData.basePar * heightFactor * powerFactor)
            
            // 估算PAR值范围（中心点±30%）
            minPar = Math.round(centerPar * 0.7)
            maxPar = Math.round(centerPar * 1.3)
          }
        } else {
          // 通用灯具的估算逻辑
          const basePar = parData.lightPower * 2
          const lightTypeMultipliers = {
            led: 1.2,
            t5: 1.0,
            metal_halide: 1.5
          }
          const multiplier = lightTypeMultipliers[parData.lightType as keyof typeof lightTypeMultipliers] || 1.0
          const heightFactor = Math.max(0.3, 1 - (parData.lightHeight - 20) / 100)
          const centerPar = Math.round(basePar * multiplier * heightFactor)
          
          minPar = Math.round(centerPar * 0.7)
          maxPar = Math.round(centerPar * 1.3)
        }
        
        if (minPar > 0 && maxPar > 0) {
          lightingInfo.push(`灯高${parData.lightHeight}cm 中心点PAR值范围: ${minPar}-${maxPar} μmol/m²/s`)
        }
      }
      
      // 注意：根据用户要求，只显示中心点PAR值范围，不显示实际测量值
      
      if (lightingInfo.length > 0) {
        formData.lighting = lightingInfo.join(' | ')
      }
    }
    
    showImportDialog.value = false
    showSuccessModalMessage(t('aiAdvisor.successMessages.importSuccess').value)
  } catch (error) {
    console.error('导入数据失败:', error)
    showSuccessModalMessage(t('aiAdvisor.successMessages.importFailed').value)
  }
}

// 检查是否有可导入的数据
const checkImportableData = async () => {
  try {
    const hasTankLogData = !!(await getLatestTankLogData())
    const hasPARData = !!getLatestPARData()
    showImportButton.value = hasTankLogData || hasPARData
    importDataPreview.value = {
      hasTankLogData,
      hasPARData
    }
  } catch (error) {
    console.error('检查可导入数据失败:', error)
    showImportButton.value = false
    importDataPreview.value = {
      hasTankLogData: false,
      hasPARData: false
    }
  }
}

// 打开导入对话框时更新数据预览
const openImportDialog = async () => {
  showImportDialog.value = true
  await checkImportableData()
}

// 显示成功模态框
const showSuccessModalMessage = (message: string) => {
  successModalMessage.value = message
  showSuccessModal.value = true
}

// 组件挂载
onMounted(async () => {
  initializeQuestionCounter()
  questionCounter.value = getQuestionCount()
  await checkImportableData()
})
</script>

<style scoped>
.calculator-module {
  background: white;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
}

.module-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 1.25rem 1.5rem;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: all 0.3s ease;
}

.module-header:hover {
  background: linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 20px rgba(102, 126, 234, 0.3);
}

.module-header h2 {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
  letter-spacing: -0.5px;
}

.toggle-icon {
  font-size: 1rem;
  transition: transform 0.3s ease;
  opacity: 0.8;
}

.module-content {
  padding: 0 20px;
}

.ai-intro {
  text-align: center;
  margin-bottom: 1.5rem;
  padding: 1.5rem;
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%);
  border-radius: 8px;
  border: 1px solid rgba(102, 126, 234, 0.2);
}

.ai-intro h3 {
  margin-bottom: 0.75rem;
  font-size: 1.75rem;
  font-weight: 700;
  color: #667eea;
}

.ai-intro p {
  color: #666;
  font-size: 1.1rem;
  font-weight: 400;
  line-height: 1.5;
}

.ai-form {
  margin-bottom: 1.5rem;
}

.form-section {
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid rgba(102, 126, 234, 0.1);
}

.form-section:last-of-type {
  border-bottom: none;
  margin-bottom: 1rem;
}

.form-section h4 {
  color: #495057;
  margin-bottom: 1rem;
  font-size: 1.1rem;
  font-weight: 600;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid rgba(102, 126, 234, 0.2);
}

.form-group {
  margin-bottom: 1rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.4rem;
  font-weight: 500;
  color: #495057;
  font-size: 0.85rem;
}

.form-control {
  width: 100%;
  padding: 0.75rem;
  border: 2px solid #e9ecef;
  border-radius: 6px;
  font-size: 0.9rem;
  transition: all 0.3s ease;
  background: white;
}

.form-control:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  transform: translateY(-1px);
}

.select-control {
  width: 100%;
  padding: 0.75rem;
  border: 2px solid #e9ecef;
  border-radius: 6px;
  font-size: 0.9rem;
  background: white;
  cursor: pointer;
  transition: all 0.3s ease;
}

.select-control:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  transform: translateY(-1px);
}

.form-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 0.75rem;
}

.question-counter {
  text-align: center;
  padding: 0.75rem;
  background: linear-gradient(135deg, #fff3cd 0%, #ffeaa7 100%);
  border: 2px solid #ffeaa7;
  border-radius: 6px;
  margin-bottom: 1rem;
  color: #856404;
  font-weight: 500;
  font-size: 0.9rem;
}

.submit-btn {
  width: 100%;
  padding: 1rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-bottom: 1rem;
  position: relative;
  overflow: hidden;
}

.submit-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%);
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(102, 126, 234, 0.4);
}

.submit-btn:disabled {
  background: #adb5bd;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.loading {
  display: inline-block;
  width: 18px;
  height: 18px;
  border: 2px solid #ffffff;
  border-radius: 50%;
  border-top-color: transparent;
  animation: spin 1s ease-in-out infinite;
  margin-right: 10px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* 响应式设计 */
@media (max-width: 768px) {
  .module-content {
    padding: 0 20px;
  }
  
  .ai-intro {
    padding: 1rem;
    margin-bottom: 1rem;
  }
  
  .ai-intro h3 {
    font-size: 1.25rem;
  }
  
  .ai-intro p {
    font-size: 0.9rem;
  }
  
  .form-section h4 {
    font-size: 1rem;
  }
  
  .form-row {
    grid-template-columns: 1fr;
    gap: 0.5rem;
  }
  
  .form-control,
  .select-control {
    padding: 0.625rem;
    font-size: 0.85rem;
  }
  
  .form-group {
    margin-bottom: 0.75rem;
  }
  
  .form-group label {
    font-size: 0.8rem;
    margin-bottom: 0.3rem;
  }
}

/* 结果容器样式 */
.results-container {
  margin-top: 1.5rem;
  padding: 1rem;
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%);
  border-radius: 8px;
  border: 1px solid rgba(102, 126, 234, 0.1);
}

.empty-state {
  text-align: center;
  color: #6c757d;
  padding: 1.5rem;
}

.empty-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
  opacity: 0.5;
}

.empty-state h4 {
  margin-bottom: 0.5rem;
  color: #495057;
  font-weight: 600;
}

.empty-state p {
  margin: 0;
  font-size: 0.95rem;
}

.ai-review {
  background: white;
  padding: 1rem;
  border-radius: 6px;
  border: 1px solid #e9ecef;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.ai-review h4 {
  color: #667eea;
  margin-bottom: 1rem;
  font-size: 1.25rem;
  font-weight: 600;
  border-bottom: 2px solid #667eea;
  padding-bottom: 0.5rem;
}

.review-content h5 {
  color: #495057;
  margin: 1.5rem 0 0.75rem 0;
  font-size: 1.1rem;
  font-weight: 600;
}

.review-content p {
  margin-bottom: 0.75rem;
  line-height: 1.6;
  color: #495057;
}

.limit-message,
.error-message {
  background: white;
  padding: 1rem;
  border-radius: 6px;
  border: 1px solid #e9ecef;
  text-align: center;
}

.limit-message h4,
.error-message h4 {
  color: #dc3545;
  margin-bottom: 1rem;
}

.limit-message p,
.error-message p {
  margin-bottom: 0.5rem;
  color: #495057;
}

.limit-note {
  font-size: 0.9rem;
  color: #6c757d;
  font-style: italic;
}

.loading-spinner {
  text-align: center;
  padding: 1.5rem;
}

.spinner {
  display: inline-block;
  width: 40px;
  height: 40px;
  border: 4px solid rgba(102, 126, 234, 0.3);
  border-radius: 50%;
  border-top-color: #667eea;
  animation: spin 1s ease-in-out infinite;
  margin-bottom: 1rem;
}

.loading-spinner p {
  color: #495057;
  margin: 0;
}

/* 导入功能样式 */
.import-section {
  margin-bottom: 1rem;
  text-align: center;
}

.import-btn {
  background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
  color: white;
  border: none;
  padding: 0.75rem 1.25rem;
  border-radius: 6px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(40, 167, 69, 0.3);
}

.import-btn:hover {
  background: linear-gradient(135deg, #218838 0%, #1e9e8a 100%);
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(40, 167, 69, 0.4);
}

.import-dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
  overflow-y: auto;
}

.import-dialog-overlay::before {
  content: '';
  display: block;
  height: 20px;
  flex-shrink: 0;
}

.import-dialog {
  background: white;
  border-radius: 12px;
  padding: 0;
  max-width: 500px;
  width: 100%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: dialogSlideIn 0.3s ease-out;
  margin-top: 20px;
  margin-bottom: 20px;
}

@keyframes dialogSlideIn {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.dialog-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 1.5rem 1rem;
  border-bottom: 1px solid #e9ecef;
}

.dialog-header h4 {
  margin: 0;
  color: #2c3e50;
  font-size: 1.25rem;
}

.close-btn {
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #6c757d;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.close-btn:hover {
  background: #f8f9fa;
  color: #495057;
}

.dialog-content {
  padding: 1.5rem;
}

.dialog-content p {
  margin: 0 0 1.5rem 0;
  color: #495057;
  line-height: 1.5;
}

.data-preview {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 1.25rem;
}

.data-preview h5 {
  margin: 0 0 1rem 0;
  color: #2c3e50;
  font-size: 1rem;
}

.data-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
  border-bottom: 1px solid #e9ecef;
}

.data-item:last-child {
  border-bottom: none;
}

.data-label {
  color: #6c757d;
  font-weight: 500;
}

.data-value {
  color: #495057;
  font-weight: 600;
}

.no-data {
  text-align: center;
  color: #6c757d;
  font-style: italic;
  padding: 1rem 0;
}

.dialog-actions {
  display: flex;
  gap: 1rem;
  padding: 1rem 1.5rem;
  border-top: 1px solid #e9ecef;
  justify-content: flex-end;
}

.cancel-btn, .confirm-btn {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.cancel-btn {
  background: #6c757d;
  color: white;
}

.cancel-btn:hover {
  background: #5a6268;
}

.confirm-btn {
  background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
  color: white;
}

.confirm-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, #218838 0%, #1e9e8a 100%);
  transform: translateY(-1px);
}

.confirm-btn:disabled {
  background: #adb5bd;
  cursor: not-allowed;
  transform: none;
}

/* 成功消息模态框样式 */
.success-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  z-index: 1001;
  padding: 1rem;
  overflow-y: auto;
}

.success-modal-overlay::before {
  content: '';
  display: block;
  height: 20px;
  flex-shrink: 0;
}

.success-modal {
  background: white;
  border-radius: 16px;
  padding: 2rem;
  max-width: 400px;
  width: 100%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  text-align: center;
  animation: modalSlideIn 0.3s ease-out;
  margin-top: 20px;
  margin-bottom: 20px;
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: translateY(-20px) scale(0.9);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.success-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
  animation: iconBounce 0.6s ease-out;
}

@keyframes iconBounce {
  0%, 20%, 50%, 80%, 100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-10px);
  }
  60% {
    transform: translateY(-5px);
  }
}

.success-content h4 {
  margin: 0 0 1rem 0;
  color: #2c3e50;
  font-size: 1.5rem;
  font-weight: 600;
}

.success-content p {
  margin: 0 0 1.5rem 0;
  color: #495057;
  line-height: 1.5;
  font-size: 1.1rem;
}

.success-close-btn {
  background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
  color: white;
  border: none;
  padding: 0.875rem 2rem;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(40, 167, 69, 0.3);
}

.success-close-btn:hover {
  background: linear-gradient(135deg, #218838 0%, #1e9e8a 100%);
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(40, 167, 69, 0.4);
}

@media (max-width: 480px) {
  .module-header {
    padding: 0.75rem 1rem;
  }
  
  .module-header h2 {
    font-size: 1.1rem;
  }
  
  .module-content {
    padding: 0 15px;
  }
  
  .ai-intro {
    padding: 0.75rem;
  }
  
  .ai-intro h3 {
    font-size: 1.1rem;
  }
  
  .submit-btn {
    padding: 0.75rem;
    font-size: 0.9rem;
  }
  
  .results-container {
    padding: 0.75rem;
  }
  
  .empty-state {
    padding: 1rem;
  }
  
  .empty-icon {
    font-size: 2rem;
  }
  
  .import-dialog-overlay {
    padding: 0.5rem;
    align-items: flex-start;
  }
  
  .import-dialog {
    margin-top: 10px;
    margin-bottom: 10px;
  }
  
  .dialog-actions {
    flex-direction: column;
  }
  
  .cancel-btn, .confirm-btn {
    width: 100%;
  }
  
  .success-modal {
    padding: 1.5rem;
    margin: 10px 0.5rem;
  }
  
  .success-icon {
    font-size: 2.5rem;
  }
  
  .success-content h4 {
    font-size: 1.25rem;
  }
  
  .success-content p {
    font-size: 1rem;
  }
}
</style>